import {
  AuthGoBackSVG,
  InputPasswordHideSVG,
  InputPasswordShowSVG,
} from "@/assets/svg";
import { successToast, errorToast } from "@/utils/toast";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

interface CreateNewPasswordFormData {
  newPassword: string;
  confirmPassword: string;
}

interface CreateNewPasswordFormProps {
  onGoBack: () => void;
  onSubmit: (data: { newPassword: string }) => Promise<void>;
}

const CreateNewPasswordForm = ({
  onGoBack,
  onSubmit,
}: CreateNewPasswordFormProps) => {
  const navigate = useNavigate();
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, isValid },
    getValues,
  } = useForm<CreateNewPasswordFormData>({
    mode: "onChange",
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  // http://localhost:3000/#/auth/generate-password?token=66e565f5-6161-4cee-827e-fd9752f90e93

  const handleFormSubmit = async (data: CreateNewPasswordFormData) => {
    try {
      const password = btoa(data.confirmPassword);

      await onSubmit({ newPassword: password });
      successToast(
        "New password created!",
        "You can now sign in using your new password."
      );
      setResetSuccess(true);

      // Redirect to sign-in page after a short delay
      setTimeout(() => {
        navigate("/#/auth/signin");
      }, 2000);
    } catch (error) {
      console.error("Password reset failed:", error);
      // errorToast("Access denied", "Password reset failed. Please try again.");
    }
  };

  return (
    <>
      <div className="mb-3">
        <button
          type="button"
          onClick={onGoBack}
          className="text-[#1E1E1E] text-[18px] font-[600] no-underline hover:underline cursor-pointer flex items-center before:text-lg"
        >
          <AuthGoBackSVG />
          Go back
        </button>
      </div>

      <h2 className="text-[40px] text-[#232323] font-bold mb-2">
        Create new password
      </h2>
      <p className="text-[#969696] text-[18px] font-[400] mb-8">
        Enter your new password
      </p>

      {resetSuccess ? (
        <div className="p-4 mb-6 bg-green-50 border border-green-200 rounded-md">
          <p className="text-green-700">
            Your password has been reset successfully. You can now sign in with
            your new password.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
          <div className="mb-6">
            <label
              htmlFor="newPassword"
              className="block mb-2 text-[#1e1e1e] font-[500] text-[14px]"
            >
              New password
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                id="newPassword"
                placeholder="Enter your new password"
                className={`w-full h-[42px] px-[10px] py-3 border rounded-[6px] text-[14px] text-[#1e1e1e] font-[400] placeholder:text-[#B3B3B3] ${
                  errors.newPassword
                    ? "border-[#FF3B30]"
                    : "border-[#E0E0E0] focus:border-[#73b5c5]"
                } focus:outline-none  focus:shadow-[0_0_0_2px_rgba(0,136,204,0.1)]`}
                {...register("newPassword", {
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters",
                  },
                  pattern: {
                    value:
                      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{6,}$/,
                    message:
                      "Password must include at least 1 uppercase, 1 lowercase, 1 number, and 1 special character",
                  },
                })}
              />
              <button
                type="button"
                className="absolute cursor-pointer right-3 top-1/2 transform -translate-y-1/2 text-[#B3B3B3] hover:text-[#73b5c5] focus:outline-none"
                onClick={() => setShowNewPassword(!showNewPassword)}
                aria-label={showNewPassword ? "Hide password" : "Show password"}
              >
                {showNewPassword ? (
                  <InputPasswordHideSVG />
                ) : (
                  <InputPasswordShowSVG />
                )}
              </button>
            </div>
            {errors.newPassword && (
              <span className="text-[#FF3B30] text-[12px] font-[500] mt-0 block">
                {errors.newPassword.message}
              </span>
            )}
          </div>

          <div className="mb-6">
            <label
              htmlFor="confirmPassword"
              className="block mb-2 text-[#1e1e1e] font-[500] text-[14px]"
            >
              Confirm new password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                placeholder="Confirm your new password"
                className={`w-full h-[42px] px-[10px] py-3 border rounded-[6px] text-[14px] text-[#1e1e1e] font-[400] placeholder:text-[#B3B3B3] ${
                  errors.confirmPassword
                    ? "border-[#FF3B30]"
                    : "border-[#E0E0E0] focus:border-[#73b5c5]"
                } focus:outline-none  focus:shadow-[0_0_0_2px_rgba(0,136,204,0.1)]`}
                {...register("confirmPassword", {
                  required: "Please confirm your password",
                  validate: (value) =>
                    value === getValues("newPassword") ||
                    "Password does not match",
                })}
              />
              <button
                type="button"
                className="absolute cursor-pointer right-3 top-1/2 transform -translate-y-1/2 text-[#B3B3B3] hover:text-[#73b5c5] focus:outline-none"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={
                  showConfirmPassword ? "Hide password" : "Show password"
                }
              >
                {showConfirmPassword ? (
                  <InputPasswordHideSVG />
                ) : (
                  <InputPasswordShowSVG />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <span className="text-[#FF3B30] text-[12px] font-[500] mt-0 block">
                {errors.confirmPassword.message}
              </span>
            )}
          </div>

          <button
            type="submit"
            className="mt-6 w-full h-[42px] bg-[#73b5c5] text-white border-none rounded-[4px] text-[18px] font-[600] cursor-pointer transition-colors hover:bg-[#5a9aaa] disabled:bg-[#9e9e9e] disabled:cursor-not-allowed"
            disabled={
              isSubmitting ||
              !isValid ||
              !watch("newPassword") ||
              !watch("confirmPassword") ||
              Object.keys(errors).length > 0
            }
          >
            {isSubmitting ? "Creating password..." : "Create new password"}
          </button>
        </form>
      )}
    </>
  );
};

export default CreateNewPasswordForm;
